import React, { useEffect, useRef, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { createResourceLocatorString } from '../../util/routes';

import {
  Heading,
  H2,
  H3,
  Page,
  LayoutSingleColumn,
  NamedLink,
  PrimaryButton,
  IconCheckmark,
  ListingCard,
} from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import Footer from '../../components/Footer/Footer';

import heroImage from '../../assets/81fd04d0fafd80afd65494ef1178488cd64ce577.avif';

import css from './HomePage.module.css';

const CATEGORIES = [
  {
    key: 'airbnb',
    label: 'Airbnb',
    count: '52 owners',
    image:
      'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=600&h=600&fit=crop&q=80',
  },
  {
    key: 'nail-salon',
    label: 'Nail Salon',
    count: '24 owners',
    image:
      'https://images.unsplash.com/photo-1604654894610-df63bc536371?w=600&h=600&fit=crop&q=80',
  },
  {
    key: 'kids-activities',
    label: 'Kids Activities',
    count: '11 owners',
    image:
      'https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?w=600&h=600&fit=crop&q=80',
  },
  {
    key: 'laundromats',
    label: 'Laundromats',
    count: '9 owners',
    image:
      'https://images.unsplash.com/photo-1545173168-9f1947eebb7f?w=600&h=600&fit=crop&q=80',
  },
  {
    key: 'event-space',
    label: 'Event Space',
    count: '18 owners',
    image:
      'https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?w=600&h=600&fit=crop&q=80',
  },
  {
    key: 'coffee-shop',
    label: 'Coffee Shop',
    count: '31 owners',
    image:
      'https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=600&h=600&fit=crop&q=80',
  },
  {
    key: 'fitness-studio',
    label: 'Fitness Studio',
    count: '14 owners',
    image:
      'https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=600&h=600&fit=crop&q=80',
  },
];

const STATS = [
  { value: '8,500+', label: 'calls booked' },
  { value: '1,200+', label: 'verified owners' },
  { value: '4.9', label: 'average rating', suffix: '/5' },
  { value: '23 mins', label: 'median wait time' },
];

const VALUE_PROPS = [
  {
    title: 'Direct conversations',
    description:
      'Talk one-on-one with vetted business owners. No agencies, no cold DMs, no funnels.',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M3 6.5C3 5.12 4.12 4 5.5 4h13a2.5 2.5 0 0 1 2.5 2.5v8a2.5 2.5 0 0 1-2.5 2.5H10l-4 3.5V17H5.5A2.5 2.5 0 0 1 3 14.5v-8Z"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
        <circle cx="9" cy="10.5" r="1" fill="currentColor" />
        <circle cx="13" cy="10.5" r="1" fill="currentColor" />
        <circle cx="17" cy="10.5" r="1" fill="currentColor" />
      </svg>
    ),
  },
  {
    title: 'Advice that fits you',
    description:
      'Get business advice tailored to where you are now — not generic YouTube tips.',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.6" />
        <path d="M12 1v3M12 20v3M1 12h3M20 12h3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    title: 'Booked, paid, protected',
    description:
      'Stripe-secured payments, refundable cancellations, and a satisfaction guarantee on every call.',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M12 2 4 5v6c0 5.5 3.84 10.18 8 11 4.16-.82 8-5.5 8-11V5l-8-3Z"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
        <path d="m8 12 3 3 5-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
];

const STEPS = [
  {
    number: '01',
    title: 'Pick a niche, pick a person',
    description:
      "Browse business owners by industry, city, or experience. See what they've built and connect directly for advice.",
  },
  {
    number: '02',
    title: 'Book a 30 or 60-minute call',
    description:
      'Choose a slot that fits. Pay securely through Stripe. You get a calendar invite and a Google Meet link in seconds.',
  },
  {
    number: '03',
    title: 'Walk away with clear next steps',
    description:
      'Get answers on costs, leases, hiring, pricing.',
  },
];

const EXPERTS = [
  {
    name: 'Anita K.',
    experience: '8 years of experience',
    niche: 'Nail Salon',
    rating: '5.0',
    reviews: 92,
    nextSlot: 'Tomorrow, 10:00 AM',
    price: 75,
    image:
      'https://images.unsplash.com/photo-1669269628135-21636aefa17d?q=80&w=928&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  },
  {
    name: 'Jordan L.',
    experience: '5 years · 4 properties',
    niche: 'Airbnb',
    rating: '4.9',
    reviews: 47,
    nextSlot: 'Today, 4:30 PM',
    price: 80,
    image:
      'https://images.unsplash.com/photo-1569779213435-ba3167dde7cc?q=80&w=1160&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  },
  {
    name: 'Sam P.',
    experience: '7 years · 3 cafés',
    niche: 'Coffee Shop',
    rating: '4.8',
    reviews: 64,
    nextSlot: 'Today, 7:15 PM',
    price: 90,
    image:
      'https://images.unsplash.com/photo-1739413323915-2a289cc024f0?q=80&w=1160&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  },
];

const TESTIMONIALS = [
  {
    quote:
      'I almost signed a terrible lease. One 30-minute call saved me from a five-year mistake.',
    name: 'Maya R.',
    role: 'Aspiring Salon Owner',
    location: 'Phoenix, AZ',
  },
  {
    quote: 'YouTube gave me a hundred opinions. This call gave me one clear plan.',
    name: 'Daniel K.',
    role: 'First-Time Entrepreneur',
    location: 'Austin, TX',
  },
  {
    quote:
      'Talking to someone who actually runs multiple Airbnbs answered questions I couldn\'t find anywhere online.',
    name: 'Lauren S.',
    role: 'Aspiring Airbnb Host',
    location: 'San Diego, CA',
  },
  {
    quote:
      'Not theory. I left with exact scripts, vendor recommendations, and an opening-week checklist.',
    name: 'Priya M.',
    role: 'New Business Owner',
    location: 'Jersey City, NJ',
  },
  {
    quote:
      "I wish I'd booked this call before spending money on things I didn't need.",
    name: 'Alex T.',
    role: 'Aspiring Business Owner',
    location: 'Chicago, IL',
  },
  {
    quote:
      "Honest advice from someone who's already made the mistakes — and told me what not to do.",
    name: 'James W.',
    role: 'Coffee Shop Owner',
    location: 'Portland, OR',
  },
];

const FAQ_PREVIEW = [
  {
    question: "What if the call doesn't help?",
    answer:
      "We back every call with a satisfaction guarantee. If your expert no-shows or the call quality is poor, we refund you in full or hand you a free booking credit — no questions asked.",
  },
  {
    question: 'How are experts verified?',
    answer:
      "Every owner goes through a real vetting process. We confirm their business operations, check references, and require proof of hands-on experience in the niche they advise on.",
  },
  {
    question: 'Can I reschedule or cancel?',
    answer:
      'Yes — reschedule or cancel up to 24 hours before for a full refund. Cancellations inside 24 hours may incur a small fee so we can compensate the expert who held the slot.',
  },
];

const COMPARISON = [
  {
    label: 'Generic blogs & videos',
    description: 'Advice written for someone else, in another city, three years ago.',
    isWinner: false,
    points: ['One-size-fits-all', 'No accountability', 'Often outdated', 'No follow-up'],
  },
  {
    label: 'DoneThat',
    description: "A live conversation with someone who's already built the business you're thinking about.",
    isWinner: true,
    points: [
      'Live 1-on-1 video',
      'Verified business owners',
      'Tailored to your city',
      'Money-back guarantee',
    ],
  },
  {
    label: 'Free Facebook groups',
    description: 'Strangers with strong opinions and zero stake in your outcome.',
    isWinner: false,
    points: ['Unverified sources', 'Conflicting advice', 'No accountability', 'Hours of scrolling'],
  },
];

const TRUST_LINE = ['Stripe-secured payments', 'SSL encrypted', 'Money-back guarantee', 'Verified business owners'];

const FaqItem = ({ question, answer, isOpen, onToggle }) => (
  <div className={`${css.faqItem} ${isOpen ? css.faqItemOpen : ''}`}>
    <button className={css.faqQuestion} onClick={onToggle} aria-expanded={isOpen}>
      <span>{question}</span>
      <span className={`${css.faqIcon} ${isOpen ? css.faqIconOpen : ''}`} aria-hidden>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M7 1v12M1 7h12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </span>
    </button>
    <div className={`${css.faqAnswer} ${isOpen ? css.faqAnswerOpen : ''}`}>
      <p>{answer}</p>
    </div>
  </div>
);

export const HomePageComponent = props => {
  const { scrollingDisabled, listings = [] } = props;
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();

  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const isHoveringRef = useRef(false);

  useEffect(() => {
    const timer = setInterval(() => {
      if (!isHoveringRef.current) {
        setActiveTestimonial(prev => (prev + 1) % TESTIMONIALS.length);
      }
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const title = intl.formatMessage({
    id: 'HomePage.title',
    defaultMessage: 'DoneThat — Talk to Real Business Owners',
  });

  const handleFindOwner = () => {
    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, {}));
  };

  const handleCategoryClick = categoryKey => {
    history.push(
      createResourceLocatorString('SearchPage', routeConfiguration, {}, { pub_categoryLevel1: categoryKey })
    );
  };

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<Footer />}>
        <div className={css.root}>
          {/* ===== Hero ===== */}
          <section className={css.hero}>
            <img src={heroImage} alt="" className={css.heroBgPhoto} aria-hidden="true" />
            <div className={css.heroOverlay} aria-hidden />
            <div className={css.heroBg} aria-hidden />
            <div className={css.heroInner}>
              <div className={css.heroLeft}>
                <Heading as="h1" rootClassName={css.heroHeading}>
                Talk to business owners who&apos;ve <em className={css.heroEm}>been there</em> before making your next move
                </Heading>
                <p className={css.heroDescription}>
                  Get actionable, real-world advice through 1-on-1 video calls with verified business owners.
                </p>
                <div className={css.heroActions}>
                  <PrimaryButton className={css.heroCta} onClick={handleFindOwner}>
                    Find a business owner
                    <svg
                      className={css.heroCtaArrow}
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      aria-hidden
                    >
                      <path
                        d="M3 8h10m0 0L8.5 3.5M13 8l-4.5 4.5"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </PrimaryButton>
                  <NamedLink name="HowItWorksPage" className={css.heroSecondary}>
                    See how it works
                  </NamedLink>
                </div>
                <div className={css.heroSocialProof}>
                  <span className={css.heroSocialProofPulse} aria-hidden />
                  <span className={css.heroSocialProofText}>
                    <strong>24 calls booked</strong> in the last 48 hours
                  </span>
                </div>
              </div>
            </div>
          </section>

          {/* ===== Stats strip ===== */}
          {/* <section className={css.stats}>
            <div className={css.statsInner}>
              {STATS.map((s, i) => (
                <div key={i} className={css.statItem}>
                  <span className={css.statValue}>
                    {s.value}
                    {s.suffix && <em>{s.suffix}</em>}
                  </span>
                  <span className={css.statLabel}>{s.label}</span>
                </div>
              ))}
            </div>
          </section> */}

          {/* ===== Categories ===== */}
          <section className={css.categories}>
            <div className={css.sectionHead}>
              <span className={css.eyebrow}>Browse by niche</span>
              <H2 rootClassName={css.sectionHeading}>
                Find someone who&apos;s grown the business <em>you own</em> — or want to build
              </H2>
            </div>
            <div className={css.categoriesScroll}>
              {CATEGORIES.map(cat => (
                <button
                  key={cat.key}
                  className={css.categoryCard}
                  onClick={() => handleCategoryClick(cat.key)}
                >
                  <span
                    className={css.categoryImage}
                    style={{ backgroundImage: `url(${cat.image})` }}
                    aria-hidden
                  />
                  <span className={css.categoryOverlay} aria-hidden />
                  <span className={css.categoryBody}>
                    <span className={css.categoryLabel}>{cat.label}</span>
                    {/* <span className={css.categoryCount}>{cat.count}</span> */}
                  </span>
                </button>
              ))}
            </div>
          </section>

          {/* ===== Value props ===== */}
          <section className={css.valueProps}>
            <div className={css.valuePropsGrid}>
              {VALUE_PROPS.map((vp, i) => (
                <div key={i} className={css.valuePropCard}>
                  <div className={css.valuePropIcon}>{vp.icon}</div>
                  <H3 rootClassName={css.valuePropTitle}>{vp.title}</H3>
                  <p className={css.valuePropDescription}>{vp.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ===== How it works ===== */}
          <section className={css.howItWorks}>
            <div className={css.sectionHead}>
              <span className={css.eyebrow}>How it works</span>
              <H2 rootClassName={css.sectionHeading}>
                From information overload to real answers in <em>three steps</em>
              </H2>
            </div>
            <div className={css.stepsGrid}>
              {STEPS.map((step, i) => (
                <div key={i} className={css.stepCard}>
                  <span className={css.stepNumber} aria-hidden>
                    {step.number}
                  </span>
                  <div className={css.stepBody}>
                    <H3 rootClassName={css.stepTitle}>{step.title}</H3>
                    <p className={css.stepDescription}>{step.description}</p>
                  </div>
                  {i < STEPS.length - 1 && <span className={css.stepConnector} aria-hidden />}
                </div>
              ))}
            </div>
            <NamedLink name="HowItWorksPage" className={css.learnMoreLink}>
              See the full walkthrough
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
                <path
                  d="M3 8h10m0 0L8.5 3.5M13 8l-4.5 4.5"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </NamedLink>
          </section>

          {/* ===== Featured experts ===== */}
          <section className={css.experts}>
            <div className={css.sectionHead}>
              <span className={css.eyebrow}>Hand-picked business owners</span>
              <H2 rootClassName={css.sectionHeading}>
                Meet a few of our <em>verified</em> experts
              </H2>
              <NamedLink name="SearchPage" className={css.viewAllLink}>
                View all experts
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
                  <path
                    d="M3 8h10m0 0L8.5 3.5M13 8l-4.5 4.5"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </NamedLink>
            </div>
            <div className={css.expertsGrid}>
              {listings.length > 0 ? (
                listings.map(l => (
                  <ListingCard
                    key={l.id.uuid}
                    listing={l}
                  />
                ))
              ) : (
                <p className={css.expertsEmpty}>Loading experts…</p>
              )}
            </div>
          </section>

          {/* ===== Testimonials ===== */}
          <section className={css.testimonials}>
            <div className={css.sectionHead}>
              <span className={css.eyebrow}>Real wins</span>
              <H2 rootClassName={css.sectionHeading}>
                Real conversations. <em>Real outcomes.</em>
              </H2>
            </div>
            <div
              className={css.testimonialsCarousel}
              onMouseEnter={() => { isHoveringRef.current = true; }}
              onMouseLeave={() => { isHoveringRef.current = false; }}
            >
              <div
                className={css.testimonialsTrack}
                style={{ transform: `translateX(-${activeTestimonial * 100}%)` }}
              >
                {TESTIMONIALS.map((t, i) => (
                  <div key={i} className={css.testimonialCard}>
                    <span className={css.quoteMark} aria-hidden>
                      &ldquo;
                    </span>
                    <p className={css.testimonialQuote}>{t.quote}</p>
                    <div className={css.testimonialAuthor}>
                      <strong>{t.name}</strong>
                      <span>
                        {t.role} · {t.location}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className={css.testimonialDots}>
              {TESTIMONIALS.map((_, i) => (
                <button
                  key={i}
                  className={`${css.dot} ${i === activeTestimonial ? css.dotActive : ''}`}
                  onClick={() => setActiveTestimonial(i)}
                  aria-label={`Testimonial ${i + 1}`}
                />
              ))}
            </div>
          </section>

          {/* ===== Comparison ===== */}
          <section className={css.comparison}>
            <div className={css.sectionHead}>
              <span className={css.eyebrow}>Why not just Google it?</span>
              <H2 rootClassName={css.sectionHeading}>
                Generic advice is <em>cheap</em>. The <em>right</em> advice is rare.
              </H2>
              <p className={css.comparisonSubtitle}>
                Most online advice isn&apos;t meant for your business, budget, or market.
              </p>
            </div>
            <div className={css.comparisonGrid}>
              {COMPARISON.map((col, i) => (
                <div
                  key={i}
                  className={`${css.comparisonCard} ${col.isWinner ? css.comparisonWinner : ''}`}
                >
                  {col.isWinner && <span className={css.comparisonBadge}>Recommended</span>}
                  <H3 rootClassName={css.comparisonLabel}>{col.label}</H3>
                  <p className={css.comparisonDesc}>{col.description}</p>
                  <ul className={css.comparisonPoints}>
                    {col.points.map((point, j) => (
                      <li key={j} className={css.comparisonPoint}>
                        <span
                          className={`${css.comparisonCheck} ${col.isWinner ? '' : css.comparisonCheckDim}`}
                          aria-hidden
                        >
                          {col.isWinner ? (
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                              <path
                                d="m2.5 6 2.5 2.5L9.5 3.5"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          ) : (
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                              <path
                                d="M3 3l6 6M9 3l-6 6"
                                stroke="currentColor"
                                strokeWidth="1.6"
                                strokeLinecap="round"
                              />
                            </svg>
                          )}
                        </span>
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          {/* ===== FAQ ===== */}
          <section className={css.faq}>
            <div className={css.faqLayout}>
              <div className={css.faqIntro}>
                <span className={css.eyebrow}>Common questions</span>
                <H2 rootClassName={css.sectionHeadingLeft}>
                  Things people ask before <em>their first call</em>
                </H2>
                <NamedLink name="FAQPage" className={css.viewAllLinkAlt}>
                  Read every answer
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
                    <path
                      d="M3 8h10m0 0L8.5 3.5M13 8l-4.5 4.5"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </NamedLink>
              </div>
              <div className={css.faqList}>
                {FAQ_PREVIEW.map((item, i) => (
                  <FaqItem
                    key={i}
                    question={item.question}
                    answer={item.answer}
                    isOpen={openFaqIndex === i}
                    onToggle={() => setOpenFaqIndex(openFaqIndex === i ? null : i)}
                  />
                ))}
              </div>
            </div>
          </section>

          {/* ===== CTA ===== */}
          <section className={css.cta}>
            <div className={css.ctaPattern} aria-hidden />
            <div className={css.ctaInner}>
              <span className={css.ctaEyebrow}>Starting out — or already in it?</span>
              <H2 rootClassName={css.ctaHeading}>
                Talk to someone who&apos;s <br /><em>been there</em>.
              </H2>
              <p className={css.ctaSubtitle}>
                Skip the hundred-tab rabbit hole. Get real advice and walk away with clear next steps.
              </p>
              <PrimaryButton className={css.ctaButton} onClick={handleFindOwner}>
                Find an owner
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
                  <path
                    d="M3 8h10m0 0L8.5 3.5M13 8l-4.5 4.5"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </PrimaryButton>
              <ul className={css.ctaTrust}>
                {TRUST_LINE.map((label, i) => (
                  <li key={i} className={css.ctaTrustItem}>
                    <IconCheckmark size="small" className={css.ctaTrustIcon} />
                    {label}
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const EnhancedHomePage = props => {
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  const history = useHistory();
  const intl = useIntl();

  return (
    <HomePageComponent
      routeConfiguration={routeConfiguration}
      config={config}
      history={history}
      intl={intl}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { listingIds = [] } = state.HomePage || {};
  const listings = getListingsById(state, listingIds);

  return {
    scrollingDisabled: isScrollingDisabled(state),
    listings,
  };
};

const HomePage = compose(connect(mapStateToProps))(EnhancedHomePage);

export default HomePage;
